import {
  Box,
  Container,
  Divider,
  IconButton,
  Link,
  AppBar as MUIAppBar,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { useCallback, useContext } from "react";
import moment from "moment";
import config from "../config";
import Hidden from "./HiddenSSRCompatible";
import { useRouter } from "next/router";
import MobileMenu from "./MobileMenu";
import OrganizationSwitcher from "./organization/OrganizationSwitcher";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import { StoreContext } from "../store";
import useTranslation from "next-translate/useTranslation";
import ImpersonateCard from "./ImpersonateCard";
import WhatsappAccountSwitcher from "./whatsapp/partials/WhatsappAccountSwitcher";
import LanguageSelector from "./misc/LanguageSelector";
import TopAnnouncement from "./announcement/TopAnnouncement";

function EnvironmentBar() {
  const { t } = useTranslation("common");
  return config.DEMO_MODE || config.NODE_ENV === "development" ? (
    <Box
      color="primary.contrastText"
      bgcolor={config.DEMO_MODE ? "success.dark" : "grey.700"}
      fontSize="caption.fontSize"
      textAlign="center"
      py={0.2}
    >
      {config.DEMO_MODE ? t("Demonstration mode") : t("Development mode")}
    </Box>
  ) : null;
}

function MainToolbar({ maxWidth, onSignOut, currentTab }) {
  const { t } = useTranslation("common");
  const router = useRouter();
  const store = useContext(StoreContext);
  // const showWhatsappSwitcher = !store.subscription.isAccessExpired && store.subscription.selected?.plan === "premium";
  const showWhatsappSwitcher = !store.subscription.isAccessExpired;

  if (!store.user?.signedIn) {
    return null;
  }
  return (
    <>
      <Hidden smDown>
        <Box ml={7}>
          <Toolbar disableGutters>
            <Container maxWidth="xl">
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box fontSize="h5.fontSize" className="shepherd-currentPage">
                  {t(`${currentTab}`)}
                </Box>
                {/* Only show WhatsappAccountSwitcher on the dedicated WhatsApp page */}
                {(router.pathname === "/chatwhatsapp" || router.asPath?.startsWith("/chatwhatsapp")) &&
                  showWhatsappSwitcher && <WhatsappAccountSwitcher />}
                <Box display="flex" alignItems="center">
                  {/* <OrganizationNotifications /> */}
                  <OrganizationSwitcher index={1} />
                  {/* <Tooltip title={t('Page Guide')} aria-label="page-guide">
                    <IconButton
                      aria-label="page-guide"
                      onClick={handleStartTour}
                      color="default"
                      data-cy="pageguide"
                    >
                      <HelpOutlineIcon />
                    </IconButton>
                  </Tooltip> */}
                  <LanguageSelector />
                  <Tooltip title={t("Sign out")} aria-label="sign out">
                    <IconButton aria-label="sign out" onClick={onSignOut} color="default" data-cy="signout">
                      <PowerSettingsNewIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Container>
          </Toolbar>
        </Box>
      </Hidden>
      <Hidden mdUp>
        <MobileMenu>
          <Toolbar disableGutters>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              {/* <OrganizationNotifications /> */}
              <OrganizationSwitcher index={0} />
              <Box>
                <LanguageSelector />
                <IconButton aria-label="sign out" onClick={onSignOut} color="inherit" data-cy="signout">
                  {/* On mobile, render the switcher only when on the whatsapp page */}
                  {(router.pathname === "/chatwhatsapp" || router.asPath?.startsWith("/chatwhatsapp")) &&
                    showWhatsappSwitcher && (
                      <Box mr={1} display="inline-block">
                        <WhatsappAccountSwitcher />
                      </Box>
                    )}
                  <PowerSettingsNewIcon />
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </MobileMenu>
      </Hidden>
    </>
  );
}

function TrialBar() {
  const { t } = useTranslation("common");
  const store = useContext(StoreContext);
  const router = useRouter();
  const trialEndDate = store.user?.trialEndDate;
  const today = moment();
  const userEmail = store.user?.email || "";

  const daysRemaining = moment(trialEndDate).diff(today, "days");

  const handleSubscriptionClick = useCallback(
    (e) => {
      e.preventDefault();
      router.push("/subscription");
    },
    [router],
  );

  return store.user.plan === "trial" && store.user.trialEndDate ? (
    <>
      <Hidden smDown>
        <Box boxShadow={3} bgcolor="#FFD140">
          <Container>
            <Box paddingX={0.5} paddingY={0.25} display="flex" justifyContent="center">
              <Typography>
                {t("Your account is still in trial period, active until {{trialEndDate}}.", {
                  trialEndDate: moment(store.subscription.selected?.period?.end).format("ll"),
                })}
                {t(" Click")}
                <Link
                  component="button"
                  onClick={handleSubscriptionClick}
                  color="error"
                  underline="none"
                  style={{
                    marginLeft: "5px",
                    marginRight: "5px",
                    cursor: "pointer",
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 700,
                    fontSize: "15px",
                    textDecoration: "underline",
                  }}
                >
                  {t("here")}
                </Link>
                {t("to subscribe")}
              </Typography>
            </Box>
          </Container>
        </Box>
      </Hidden>
      <Hidden mdUp>
        <Box boxShadow={2} bgcolor="#FFD140">
          <Container>
            <Typography>
              {t("Your account is still in trial period, active until {{trialEndDate}}.", {
                trialEndDate: moment(store.user.trialEndDate).format("ll"),
              })}
              {t(" Click")}
              <Link
                component="button"
                onClick={handleSubscriptionClick}
                color="blue"
                underline="none"
                style={{
                  marginLeft: "5px",
                  marginRight: "5px",
                  cursor: "pointer",
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: "15px",
                  textDecoration: "underline",
                }}
              >
                {t("here")}
              </Link>
              {t("to subscribe")}
            </Typography>
          </Container>
        </Box>
      </Hidden>
    </>
  ) : daysRemaining <= 28 && store.user.trialEndDate ? (
    <>
      <Hidden smDown>
        <Box boxShadow={3} bgcolor="#FFD140">
          <Container>
            <Box paddingX={0.5} paddingY={0.25} display="flex" justifyContent="center">
              <Typography>
                Permisi, Akun Anda akan kedaluwarsa pada {moment(store.user.trialEndDate).format("ll")}, tersisa{" "}
                {daysRemaining} hari.
                {t(" Click")}
                <Link
                  component="button"
                  onClick={handleSubscriptionClick}
                  color="error"
                  underline="none"
                  style={{
                    marginLeft: "5px",
                    marginRight: "5px",
                    cursor: "pointer",
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 700,
                    fontSize: "15px",
                    textDecoration: "underline",
                  }}
                >
                  {t("here")}
                </Link>
                untuk meneruskan berlangganan.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Hidden>
      <Hidden mdUp>
        <Box boxShadow={2} bgcolor="#FFD140">
          <Container>
            <Typography>
              Permisi, Akun Anda akan kedaluwarsa pada {moment(store.user.trialEndDate).format("ll")}, tersisa{" "}
              {daysRemaining} hari.
              {t(" Click")}
              <Link
                component="button"
                onClick={handleSubscriptionClick}
                color="blue"
                underline="none"
                style={{
                  marginLeft: "5px",
                  marginRight: "5px",
                  cursor: "pointer",
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: "15px",
                  textDecoration: "underline",
                }}
              >
                {t("here")}
              </Link>
              untuk meneruskan berlangganan.
            </Typography>
          </Container>
        </Box>
      </Hidden>
    </>
  ) : (
    <></>
  );
}

export default function AppBar() {
  const { t } = useTranslation("common");
  const store = useContext(StoreContext);
  const router = useRouter();
  if (router.pathname === "/404") {
    return null;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleSignOut = useCallback(
    async (event) => {
      event.preventDefault();
      await store.user.signOut();
      window.location.assign(`${config.BASE_PATH}/signin`); // will be redirected to /signin
    },
    [store.user],
  );

  const pathParts = router.pathname.split("/");
  const organizationIndex = pathParts.indexOf("[organization]");
  const currentPath = pathParts[1];
  let capitalizedCurrentPath = currentPath.charAt(0).toUpperCase() + currentPath.slice(1);
  if (
    capitalizedCurrentPath === "Firstaccess" ||
    capitalizedCurrentPath === "Onboarding" /*|| capitalizedCurrentPath === "Subscription"*/
  ) {
    capitalizedCurrentPath = "";
  }
  // if (router.pathname === "/dashboard") {
  //   return (
  //     <>
  //       <Hidden mdUp>
  //         <EnvironmentBar />
  //         <TrialBar />
  //         <MainToolbar onSignOut={handleSignOut} />
  //       </Hidden>
  //       <Hidden smDown>
  //         <EnvironmentBar />
  //         <TrialBar />
  //       </Hidden>
  //     </>
  //   );
  // }

  return (
    <>
      <Hidden smDown>
        <EnvironmentBar />
        {router.pathname === "/signin" ||
        router.pathname === "/signup" ||
        router.pathname === "/firstaccess" ||
        router.pathname === "/onboarding" /*|| router.pathname === "/subscription"*/ ||
        router.pathname === "/trial-expired" ? null : (
          <>
            <TrialBar />
            <TopAnnouncement />
          </>
        )}
        {store.user?.impersonator && <ImpersonateCard />}
        {router.pathname === "/dashboard" ||
        router.pathname === "/onboarding" /*|| router.pathname === "/subscription"*/ ||
        router.pathname === "/trial-expired" ? null : (
          <MainToolbar currentTab={capitalizedCurrentPath} onSignOut={handleSignOut} />
        )}
        {/* Special logout button for trial-expired and subscription pages */}
        {router.pathname === "/trial-expired" /*|| router.pathname === "/subscription"*/ && store.user?.signedIn && (
          <Box ml={7}>
            <Toolbar disableGutters>
              <Container maxWidth="xl">
                <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%">
                  <Tooltip title={t("Sign out")} aria-label="sign out">
                    <IconButton aria-label="sign out" onClick={handleSignOut} color="default" data-cy="signout">
                      <PowerSettingsNewIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Container>
            </Toolbar>
          </Box>
        )}
      </Hidden>
      <Hidden mdUp>
        <EnvironmentBar />
        {router.pathname === "/signin" ||
        router.pathname === "/signup" ||
        router.pathname === "/firstaccess" ||
        router.pathname === "/onboarding" ||
        router.pathname === "/subscription" ||
        router.pathname === "/trial-expired" ? null : (
          <>
            <TrialBar />
            <TopAnnouncement />
          </>
        )}
        {store.user?.impersonator && <ImpersonateCard />}
        {router.pathname === "/onboarding" ||
        router.pathname === "/subscription" ||
        router.pathname === "/trial-expired" ? null : (
          <MainToolbar onSignOut={handleSignOut} />
        )}
        {/* Special logout button for trial-expired and subscription pages on mobile */}
        {(router.pathname === "/trial-expired" || router.pathname === "/subscription") && store.user?.signedIn && (
          <Box>
            <Toolbar disableGutters>
              <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%" px={2}>
                <IconButton aria-label="sign out" onClick={handleSignOut} color="inherit" data-cy="signout">
                  <PowerSettingsNewIcon />
                </IconButton>
              </Box>
            </Toolbar>
          </Box>
        )}
      </Hidden>
    </>
  );
}
